<template>
  <div class="error-boundary">
    <div v-if="hasError" class="error-boundary-fallback">
      <div class="alert alert-danger" role="alert">
        <h4 class="alert-heading">
          <i class="fas fa-exclamation-triangle"></i>
          เกิดข้อผิดพลาด
        </h4>
        <p>{{ errorMessage }}</p>
        <hr>
        <div class="d-flex justify-content-between">
          <button 
            class="btn btn-outline-danger btn-sm" 
            @click="showDetails = !showDetails"
          >
            {{ showDetails ? 'ซ่อนรายละเอียด' : 'แสดงรายละเอียด' }}
          </button>
          <button 
            class="btn btn-primary btn-sm" 
            @click="retry"
          >
            ลองใหม่
          </button>
        </div>
        
        <div v-if="showDetails" class="mt-3">
          <small class="text-muted">
            <strong>Component:</strong> {{ errorInfo.component || 'Unknown' }}<br>
            <strong>Error:</strong> {{ errorInfo.message }}<br>
            <strong>Time:</strong> {{ errorInfo.timestamp }}
          </small>
          <details class="mt-2">
            <summary class="text-muted">Stack Trace</summary>
            <pre class="small text-muted">{{ errorInfo.stack }}</pre>
          </details>
        </div>
      </div>
    </div>
    
    <slot v-else></slot>
  </div>
</template>

<script>
import errorService from '@/services/ErrorService';

export default {
  name: 'ErrorBoundary',
  props: {
    fallbackMessage: {
      type: String,
      default: 'เกิดข้อผิดพลาดในการแสดงผลส่วนนี้'
    },
    onError: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      hasError: false,
      errorInfo: {},
      showDetails: false,
      retryKey: 0
    };
  },
  computed: {
    errorMessage() {
      return this.errorInfo.userMessage || this.fallbackMessage;
    }
  },
  errorCaptured(err, instance, info) {
    this.hasError = true;
    this.errorInfo = {
      message: err.message,
      stack: err.stack,
      component: instance?.$options.name || instance?.$options._componentTag || 'Unknown',
      info,
      timestamp: new Date().toLocaleString('th-TH'),
      userMessage: this.fallbackMessage
    };
    
    // Handle error through centralized service
    errorService.handleError(err, {
      source: 'error_boundary',
      component: this.errorInfo.component,
      boundaryName: this.$options.name,
      info
    });
    
    // Call custom error handler if provided
    if (this.onError) {
      this.onError(err, instance, info);
    }
    
    // Prevent error from propagating further
    return false;
  },
  methods: {
    retry() {
      this.hasError = false;
      this.errorInfo = {};
      this.showDetails = false;
      this.retryKey += 1;
      
      // Force re-render of child components
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    
    reset() {
      this.retry();
    }
  }
};
</script>

<style scoped>
.error-boundary-fallback {
  margin: 1rem 0;
}

.error-boundary-fallback pre {
  max-height: 200px;
  overflow-y: auto;
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.error-boundary-fallback details {
  cursor: pointer;
}

.error-boundary-fallback summary:hover {
  color: #495057;
}
</style>